import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCurrentUser, userApi } from "@/lib/api";
import { motion } from "framer-motion";
import { 
  Users, 
  UserCog,
  UsersRound,
  ArrowRight,
  Loader2,
  BookOpen
} from "lucide-react";

const quickActions = [
  {
    title: "Teachers",
    description: "Manage teacher accounts",
    icon: <UserCog className="w-6 h-6" />,
    href: "/admin/teachers",
  },
  {
    title: "Students",
    description: "Manage student accounts",
    icon: <Users className="w-6 h-6" />,
    href: "/admin/students",
  },
  {
    title: "Benchmark Definitions",
    description: "Manage BDL versions and scoring rubrics",
    icon: <BookOpen className="w-6 h-6" />,
    href: "/admin/benchmarks",
  },
];

export default function AdminDashboard() {
  const { data: user } = useCurrentUser();
  
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ["adminCounts"],
    queryFn: userApi.getCounts,
  });

  const stats = [
    { label: "Teachers", value: countsLoading ? "—" : counts?.teachers ?? "—", icon: <UserCog className="w-5 h-5" />, href: "/admin/teachers" },
    { label: "Students", value: countsLoading ? "—" : counts?.students ?? "—", icon: <Users className="w-5 h-5" />, href: "/admin/students" },
    { label: "Parents", value: countsLoading ? "—" : counts?.parents ?? "—", icon: <Users className="w-5 h-5" />, href: "/admin/parents" },
    { label: "Cohorts", value: countsLoading ? "—" : "Manage", icon: <UsersRound className="w-5 h-5" />, href: "/admin/cohorts" },
  ];

  return (
    <AppLayout role="admin">
      <div className="space-y-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {user ? `Admin Dashboard` : "Loading..."}
          </h1>
          <p className="text-muted-foreground">
            Manage schools, users, and system settings
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Link to={stat.href}>
                <Card className="hover:border-primary transition-colors cursor-pointer">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-primary">
                      {stat.icon}
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {stat.value === "—" ? <Loader2 className="w-6 h-6 animate-spin" /> : stat.value}
                      </p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, i) => (
              <motion.div
                key={action.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
              >
                <Link to={action.href}>
                  <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          {action.icon}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{action.title}</CardTitle>
                          <CardDescription>{action.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button variant="ghost" size="sm" className="gap-1 w-full justify-end">
                        Manage <ArrowRight className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
